import React, { useState, useEffect } from 'react';
import { useAuth } from "../services/auth.service";

const Automatizaciones = () => {
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [serverTimeUtc, setServerTimeUtc] = useState("");
  const [editingRecordId, setEditingRecordId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");
  const [auditData, setAuditData] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
    items: [],
    filters: {},
  });
  const [auditFilters, setAuditFilters] = useState({
    automationId: "",
    templateName: "",
    eventType: "",
    from: "",
    to: "",
    page: 1,
    limit: 20,
  });
  const [editForm, setEditForm] = useState({
    plantilla: "",
    hora: 0,
    minuto: 0,
    dia: 0,
    monthDiff: 0,
    yearDiff: 0,
    tipo_renovacion: 1,
    fixedNumberTest: "",
    fixedTestQuantity: 0,
    filtrosRaw: "[]",
    variableOverridesRaw: "{}",
  });
  const { user } = useAuth();

  const loadAutomatizaciones = () => {
    if (!user?.accessToken) return;
    setIsLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_API}/renovaciones/automatizaciones`, {
      headers: {
        "Content-Type": "application/json",
        "x-access-token": user.accessToken,
      },
    })
      .then(async (response) => {
        const responseBody = await response.json().catch(() => []);
        const serverDateHeader =
          response.headers.get("date") ||
          response.headers.get("Date") ||
          response.headers.get("x-server-time") ||
          response.headers.get("server-time");
        const serverTimeFromBody =
          responseBody?.serverTimeUtc ||
          responseBody?.serverTime ||
          responseBody?.meta?.serverTimeUtc ||
          responseBody?.meta?.serverTime;
        const candidateServerTime = serverDateHeader || serverTimeFromBody;
        if (candidateServerTime) {
          const parsedDate = new Date(candidateServerTime);
          if (!Number.isNaN(parsedDate.getTime())) {
            setServerTimeUtc(parsedDate.toISOString());
          } else {
            setServerTimeUtc(`${candidateServerTime}`);
          }
        } else {
          // Fallback estimate when server does not expose date/time headers.
          setServerTimeUtc(`${new Date().toISOString()} (estimado local UTC)`);
        }
        return responseBody;
      })
      .then((data) => {
        const normalizedRecords = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : [];
        setRecords(normalizedRecords);
      })
      .catch(() => {
        setRecords([]);
        setStatusMessage("No se pudo cargar la lista de automatizaciones");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadAutomatizaciones();
  }, [user?.accessToken]);

  const handleDelete = (id) => {
    if (!window.confirm("Desea eliminar esta automatización?")) return;
    fetch(`${process.env.REACT_APP_BACKEND_API}/renovaciones/automatizaciones/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "x-access-token": user.accessToken,
      },
    })
      .then((response) => response.json().catch(() => ({})))
      .then((data) => {
        if (data?.error) {
          setStatusMessage(data.error.message || "No se pudo eliminar la automatización");
          return;
        }
        setRecords((prev) => prev.filter((record) => record._id !== id));
        setStatusMessage("Automatización eliminada");
      })
      .catch(() => {
        setStatusMessage("No se pudo eliminar la automatización");
      });
  };

  const startEdit = (record) => {
    setEditingRecordId(record._id);
    setStatusMessage("");
    setEditForm({
      plantilla: `${record.plantilla || ""}`,
      hora: Number(record.hora || 0),
      minuto: Number(record.minuto ?? record.minute ?? 0),
      dia: Number(record.dia || 0),
      monthDiff: Number(record.monthDiff || 0),
      yearDiff: Number(record.yearDiff || 0),
      tipo_renovacion: Number(record.tipo_renovacion || 1),
      fixedNumberTest: `${record.fixedNumberTest || ""}`,
      fixedTestQuantity: Number(record.fixedTestQuantity || 0),
      filtrosRaw: JSON.stringify(Array.isArray(record.filtros) ? record.filtros : [], null, 2),
      variableOverridesRaw: JSON.stringify(record.variableOverrides || {}, null, 2),
    });
  };

  const closeEdit = () => {
    setEditingRecordId("");
    setIsSaving(false);
  };

  const saveEdit = async () => {
    if (!editingRecordId) return;
    let parsedFiltros = [];
    let parsedVariableOverrides = {};
    try {
      parsedFiltros = JSON.parse(editForm.filtrosRaw || "[]");
      parsedVariableOverrides = JSON.parse(editForm.variableOverridesRaw || "{}");
      if (!Array.isArray(parsedFiltros)) throw new Error("Filtros debe ser un arreglo");
      if (!parsedVariableOverrides || typeof parsedVariableOverrides !== "object" || Array.isArray(parsedVariableOverrides)) {
        throw new Error("Variable overrides debe ser un objeto");
      }
    } catch (error) {
      setStatusMessage(`Formato JSON inválido: ${error.message}`);
      return;
    }

    const payload = {
      plantilla: editForm.plantilla,
      hora: Number(editForm.hora || 0),
      minuto: Number(editForm.minuto || 0),
      dia: Number(editForm.dia || 0),
      monthDiff: Number(editForm.monthDiff || 0),
      yearDiff: Number(editForm.yearDiff || 0),
      tipo_renovacion: Number(editForm.tipo_renovacion || 1),
      fixedNumberTest: `${editForm.fixedNumberTest || ""}`.trim(),
      fixedTestQuantity: Number(editForm.fixedTestQuantity || 0),
      filtros: parsedFiltros,
      variableOverrides: parsedVariableOverrides,
    };

    setIsSaving(true);
    setStatusMessage("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API}/renovaciones/automatizaciones/${editingRecordId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": user.accessToken,
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json().catch(() => null);
      if (!response.ok || data?.error) {
        setStatusMessage(data?.error?.message || "No se pudo actualizar la automatización");
        setIsSaving(false);
        return;
      }
      const updatedRecord = data?.automatizacion || data?.data || data || payload;
      setRecords((prev) => prev.map((item) => (item._id === editingRecordId ? { ...item, ...updatedRecord } : item)));
      setStatusMessage("Automatización actualizada");
      closeEdit();
    } catch (error) {
      setStatusMessage(`No se pudo actualizar la automatización: ${error.message}`);
      setIsSaving(false);
    }
  };

  const formatDateTimeLocal = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const offsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
  };

  const fetchAuditData = async (filtersToUse = auditFilters) => {
    if (!user?.accessToken) return;
    setAuditLoading(true);
    setAuditError("");
    try {
      const params = new URLSearchParams();
      const normalized = {
        ...filtersToUse,
        page: Number(filtersToUse.page || 1),
        limit: Math.min(200, Math.max(1, Number(filtersToUse.limit || 20))),
      };
      if (normalized.automationId) params.set("automationId", normalized.automationId);
      if (normalized.templateName) params.set("templateName", normalized.templateName);
      if (normalized.eventType) params.set("eventType", normalized.eventType);
      if (normalized.from) params.set("from", new Date(normalized.from).toISOString());
      if (normalized.to) params.set("to", new Date(normalized.to).toISOString());
      params.set("page", `${normalized.page}`);
      params.set("limit", `${normalized.limit}`);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API}/renovaciones/automatizaciones/audits?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": user.accessToken,
          },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.error) {
        throw new Error(data?.error?.message || "No se pudo cargar la auditoría");
      }
      setAuditData({
        page: Number(data?.page || normalized.page),
        limit: Number(data?.limit || normalized.limit),
        total: Number(data?.total || 0),
        totalPages: Number(data?.totalPages || 0),
        hasNextPage: Boolean(data?.hasNextPage),
        hasPrevPage: Boolean(data?.hasPrevPage),
        items: Array.isArray(data?.items) ? data.items : [],
        filters: data?.filters || {},
      });
    } catch (error) {
      setAuditError(error.message || "No se pudo cargar la auditoría");
      setAuditData((prev) => ({ ...prev, items: [] }));
    } finally {
      setAuditLoading(false);
    }
  };

  const openAuditModal = (record) => {
    const nextFilters = {
      automationId: `${record?._id || ""}`,
      templateName: `${record?.plantilla || ""}`,
      eventType: "",
      from: "",
      to: "",
      page: 1,
      limit: 20,
    };
    setAuditFilters(nextFilters);
    setAuditModalOpen(true);
    fetchAuditData(nextFilters);
  };

  const applyAuditFilters = () => {
    const nextFilters = { ...auditFilters, page: 1 };
    setAuditFilters(nextFilters);
    fetchAuditData(nextFilters);
  };

  const changeAuditPage = (nextPage) => {
    const page = Math.max(1, Number(nextPage || 1));
    const nextFilters = { ...auditFilters, page };
    setAuditFilters(nextFilters);
    fetchAuditData(nextFilters);
  };

  return (
    <div className="bg-[#f8fafc] h-[calc(100vh-42px)] p-6 rounded-md w-full flex flex-col overflow-hidden">
      <div className="bg-white border border-gray-200 rounded-2xl px-6 py-5 shadow-md mb-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Automatizaciones</h2>
            <p className="text-sm text-gray-500">Administra reglas, pruebas y configuración de ejecución.</p>
          </div>
          <button
            type="button"
            onClick={loadAutomatizaciones}
            className="text-sm bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-500"
          >
            Recargar
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <span className="inline-flex items-center bg-gray-100 text-gray-700 border border-gray-300 rounded-full px-3 py-1">
            Total: {records.length}
          </span>
          <span className="inline-flex items-center bg-emerald-50 text-emerald-700 border border-emerald-300 rounded-full px-3 py-1">
            Server UTC: {serverTimeUtc || "Cargando..."}
          </span>
          {isLoading && (
            <span className="inline-flex items-center bg-amber-100 text-amber-700 border border-amber-300 rounded-full px-3 py-1">
              Cargando...
            </span>
          )}
          {statusMessage && (
            <span className="inline-flex items-center bg-blue-100 text-blue-700 border border-blue-300 rounded-full px-3 py-1">
              {statusMessage}
            </span>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-md overflow-hidden flex-1 min-h-0">
        <div className="overflow-x-auto h-full">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
              <tr>
                <th className="py-3 px-4 text-left">Plantilla</th>
                <th className="py-3 px-4 text-left">Programación</th>
                <th className="py-3 px-4 text-left">Filtros</th>
                <th className="py-3 px-4 text-left">Prueba</th>
                <th className="py-3 px-4 text-left">ID</th>
                <th className="py-3 px-4 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {records.map((record) => (
                <tr key={record._id} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                  <td className="py-3 px-4">
                    <p className="font-semibold">{record.plantilla || "-"}</p>
                    <p className="text-xs text-gray-500">Tipo renovación: {record.tipo_renovacion || 1}</p>
                  </td>
                  <td className="py-3 px-4 text-xs">
                    <p>Hora: {record.hora ?? 0}</p>
                    <p>Minuto: {record.minuto ?? record.minute ?? 0}</p>
                    <p>Día: {record.dia ?? 0}</p>
                    <p>Δ Mes: {record.monthDiff ?? 0}</p>
                    <p>Δ Año: {record.yearDiff ?? 0}</p>
                  </td>
                  <td className="py-3 px-4 text-xs">
                    {(record.filtros || []).length === 0 ? (
                      <span className="text-gray-400">Sin filtros</span>
                    ) : (
                      (record.filtros || []).map((filtro, index) => (
                        <div key={`${record._id}-filter-${index}`} className="mb-1">
                          <span className="font-semibold">{filtro.tipo}:</span> {`${filtro.valor}`}
                        </div>
                      ))
                    )}
                  </td>
                  <td className="py-3 px-4 text-xs">
                    <p><span className="font-semibold">Número:</span> {record.fixedNumberTest || "-"}</p>
                    <p><span className="font-semibold">Cantidad:</span> {record.fixedTestQuantity ?? 0}</p>
                  </td>
                  <td className="py-3 px-4 text-[11px] text-gray-500 break-all">{record._id}</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(record)}
                        className="bg-blue-600 hover:bg-blue-500 text-white font-semibold py-1.5 px-3 rounded text-xs"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(record._id)}
                        className="bg-red-600 hover:bg-red-500 text-white font-semibold py-1.5 px-3 rounded text-xs"
                      >
                        Eliminar
                      </button>
                      <button
                        type="button"
                        onClick={() => openAuditModal(record)}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-1.5 px-3 rounded text-xs"
                      >
                        Audits
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!isLoading && records.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-500">
                    No hay automatizaciones creadas
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingRecordId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30">
          <div className="bg-white w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl border border-gray-200 p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Editar automatización</h3>
              <button type="button" onClick={closeEdit} className="text-gray-500 hover:text-gray-800 text-sm">
                Cerrar
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-xs text-gray-600">
                Plantilla
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={editForm.plantilla}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, plantilla: event.target.value }))}
                />
              </label>
              <label className="text-xs text-gray-600">
                Tipo renovación
                <select
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={editForm.tipo_renovacion}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, tipo_renovacion: Number(event.target.value) || 1 }))
                  }
                >
                  <option value={1}>Por Vencer</option>
                  <option value={2}>Vencidas</option>
                </select>
              </label>
              <label className="text-xs text-gray-600">
                Hora
                <input
                  type="number"
                  min={0}
                  max={23}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={editForm.hora}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, hora: Number(event.target.value) }))}
                />
              </label>
              <label className="text-xs text-gray-600">
                Minuto
                <input
                  type="number"
                  min={0}
                  max={59}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={editForm.minuto}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, minuto: Number(event.target.value) }))}
                />
              </label>
              <label className="text-xs text-gray-600">
                Día
                <input
                  type="number"
                  min={1}
                  max={31}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={editForm.dia}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, dia: Number(event.target.value) }))}
                />
              </label>
              <label className="text-xs text-gray-600">
                Diferencia de mes
                <input
                  type="number"
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={editForm.monthDiff}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, monthDiff: Number(event.target.value) }))}
                />
              </label>
              <label className="text-xs text-gray-600">
                Diferencia de año
                <input
                  type="number"
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={editForm.yearDiff}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, yearDiff: Number(event.target.value) }))}
                />
              </label>
              <label className="text-xs text-gray-600">
                fixedNumberTest
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={editForm.fixedNumberTest}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, fixedNumberTest: event.target.value }))}
                  placeholder="50762868544"
                />
              </label>
              <label className="text-xs text-gray-600">
                fixedTestQuantity
                <input
                  type="number"
                  min={0}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={editForm.fixedTestQuantity}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, fixedTestQuantity: Number(event.target.value) || 0 }))
                  }
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <label className="text-xs text-gray-600">
                Filtros (JSON array)
                <textarea
                  rows={8}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-xs font-mono"
                  value={editForm.filtrosRaw}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, filtrosRaw: event.target.value }))}
                />
              </label>
              <label className="text-xs text-gray-600">
                Variable overrides (JSON object)
                <textarea
                  rows={8}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-xs font-mono"
                  value={editForm.variableOverridesRaw}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, variableOverridesRaw: event.target.value }))}
                />
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={closeEdit}
                className="px-4 py-2 rounded-lg text-sm bg-gray-300 text-gray-700 hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isSaving}
                onClick={saveEdit}
                className="px-4 py-2 rounded-lg text-sm bg-teal-600 text-white hover:bg-teal-500 disabled:bg-slate-300"
              >
                {isSaving ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}

      {auditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30">
          <div className="bg-white w-[96vw] max-w-6xl max-h-[92vh] overflow-y-auto rounded-xl shadow-2xl border border-gray-200 p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Audit de automatizaciones</h3>
              <button
                type="button"
                onClick={() => setAuditModalOpen(false)}
                className="text-gray-500 hover:text-gray-800 text-sm"
              >
                Cerrar
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
              <label className="text-xs text-gray-600">
                automationId
                <input
                  className="mt-1 w-full border rounded-lg px-2 py-2 text-xs"
                  value={auditFilters.automationId}
                  onChange={(event) =>
                    setAuditFilters((prev) => ({ ...prev, automationId: event.target.value }))
                  }
                />
              </label>
              <label className="text-xs text-gray-600">
                templateName
                <input
                  className="mt-1 w-full border rounded-lg px-2 py-2 text-xs"
                  value={auditFilters.templateName}
                  onChange={(event) =>
                    setAuditFilters((prev) => ({ ...prev, templateName: event.target.value }))
                  }
                />
              </label>
              <label className="text-xs text-gray-600">
                eventType
                <input
                  className="mt-1 w-full border rounded-lg px-2 py-2 text-xs"
                  placeholder="message_error"
                  value={auditFilters.eventType}
                  onChange={(event) =>
                    setAuditFilters((prev) => ({ ...prev, eventType: event.target.value }))
                  }
                />
              </label>
              <label className="text-xs text-gray-600">
                from
                <input
                  type="datetime-local"
                  className="mt-1 w-full border rounded-lg px-2 py-2 text-xs"
                  value={formatDateTimeLocal(auditFilters.from)}
                  onChange={(event) => setAuditFilters((prev) => ({ ...prev, from: event.target.value }))}
                />
              </label>
              <label className="text-xs text-gray-600">
                to
                <input
                  type="datetime-local"
                  className="mt-1 w-full border rounded-lg px-2 py-2 text-xs"
                  value={formatDateTimeLocal(auditFilters.to)}
                  onChange={(event) => setAuditFilters((prev) => ({ ...prev, to: event.target.value }))}
                />
              </label>
              <label className="text-xs text-gray-600">
                limit
                <input
                  type="number"
                  min={1}
                  max={200}
                  className="mt-1 w-full border rounded-lg px-2 py-2 text-xs"
                  value={auditFilters.limit}
                  onChange={(event) =>
                    setAuditFilters((prev) => ({ ...prev, limit: Math.min(200, Math.max(1, Number(event.target.value) || 20)) }))
                  }
                />
              </label>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <button
                type="button"
                onClick={applyAuditFilters}
                className="bg-indigo-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-indigo-500"
              >
                Aplicar filtros
              </button>
              <button
                type="button"
                onClick={() => fetchAuditData(auditFilters)}
                className="bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-xs hover:bg-gray-100"
              >
                Recargar
              </button>
              <span className="text-xs text-gray-500">
                Página {auditData.page} de {auditData.totalPages || 1} | Total: {auditData.total}
              </span>
            </div>

            {auditError && (
              <p className="text-xs text-red-600 mb-3">{auditError}</p>
            )}

            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-100 text-gray-600 uppercase">
                    <tr>
                      <th className="py-2 px-3 text-left">Fecha</th>
                      <th className="py-2 px-3 text-left">Evento</th>
                      <th className="py-2 px-3 text-left">Template</th>
                      <th className="py-2 px-3 text-left">Automation ID</th>
                      <th className="py-2 px-3 text-left">Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLoading && (
                      <tr>
                        <td colSpan={5} className="py-8 px-3 text-center text-gray-500">Cargando auditoría...</td>
                      </tr>
                    )}
                    {!auditLoading && auditData.items.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-8 px-3 text-center text-gray-500">Sin registros de auditoría</td>
                      </tr>
                    )}
                    {!auditLoading && auditData.items.map((item, index) => (
                      <tr key={`${item?._id || "audit"}-${index}`} className="border-t">
                        <td className="py-2 px-3 whitespace-nowrap">
                          {item?.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}
                        </td>
                        <td className="py-2 px-3">{item?.eventType || "-"}</td>
                        <td className="py-2 px-3">{item?.templateName || "-"}</td>
                        <td className="py-2 px-3 break-all">{item?.automationId || "-"}</td>
                        <td className="py-2 px-3">
                          <pre className="whitespace-pre-wrap break-words text-[11px] bg-gray-50 border rounded p-2 max-h-40 overflow-y-auto">
                            {JSON.stringify(item, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                disabled={!auditData.hasPrevPage}
                onClick={() => changeAuditPage((auditData.page || 1) - 1)}
                className="px-3 py-1.5 rounded text-xs bg-gray-200 text-gray-700 disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={!auditData.hasNextPage}
                onClick={() => changeAuditPage((auditData.page || 1) + 1)}
                className="px-3 py-1.5 rounded text-xs bg-indigo-600 text-white disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Automatizaciones;
